/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Route, Routes, Navigate, useNavigate } from 'react-router';
import { connect, selectors, actions, ImmutablePropTypes } from 'data';
import { isChinaConfig } from 'config';
import { getLanguage } from 'utils/language-helpers';
import Error from 'components/core/error';

import {
  basePath,
  signInPath,
  signOutPath,
  registrationPath,
  registrationSuccessPath,
  restorePasswordPath,
  recoverPasswordPath,
  restorePasswordSuccessPath,
  contactUsPath,
  contactUsSuccessPath,
  installationPath,
  termsConditionsPath,
  dataPrivacyPath,
  imprintPath,
  wechatCallbackPath,
  wechatInvitePath,
  wechatAccountChoicePath,
  wechatLoginLinkPath,
  baseErrorPath,
  helpPath,
  notFoundPath,
} from 'constants/urls';

import SignIn from 'containers/shared/sign-in';
import SignOut from 'containers/shared/sign-out';
import RestorePassword from 'containers/shared/restore-password';
import RecoverPassword from 'containers/shared/recover-password';
import RestorePasswordSuccess from 'components/shared/restore-password-success';
import Registration from 'containers/shared/registration';
import RegistrationSuccess from 'components/shared/registration-success';
import ContactUs from 'containers/shared/contact-us';
import ContactUsSuccess from 'components/shared/contact-us-success';
import Installation from 'components/shared/installation';
import Help from 'containers/shared/help';
import TermsAndConditions from 'containers/shared/terms-conditions';
import DataPrivacy from 'containers/shared/data-privacy';
import Imprint from 'containers/shared/imprint';
import WeChatCallback from 'components/shared/wechat-callback';
import WechatInvite from 'components/shared/wechat-invite';
import WeChatAccountChoice from 'containers/shared/wechat-account-choice';
import WeChatLoginLink from 'components/shared/wechat-login-link';
import SharedLayout from 'components/shared/layout';
import Header from 'components/shared/header';
import Footer from 'components/shared/footer';

const mapStateToProps = (state) => ({
  countries: selectors.profile.getCountries(state),
  countriesLoaded: selectors.profile.getCountriesLoaded(state),
  countriesLoading: selectors.profile.getCountriesLoading(state),
  languages: selectors.profile.getLanguages(state),
  languagesLoaded: selectors.profile.getLanguagesLoaded(state),
});

const mapDispatchToProps = {
  fetchCountries: actions.profile.fetchCountries,
  fetchLanguages: actions.profile.fetchLanguages,
  changeLanguage: actions.profile.changeLanguage,
};

class Shared extends React.Component {
  state = {
    selectedCountry: { label: 'Belgium', value: 'BE' },
  };

  async componentDidMount() {
    await this.props.fetchLanguages();
    const detectedLang = this.props.languages
      .find((lang) => lang.get('value') === getLanguage().toLowerCase())
      ?.get('value');

    if (!detectedLang) await this.handleSelectLanguage(isChinaConfig ? 'cn' : 'en-US');

    if (!this.props.countriesLoading) this.props.fetchCountries();
  }

  handleSelectLanguage = async (language) => {
    await this.props.changeLanguage(language);
    this.props.i18n.changeLanguage(language);
  };

  handleHeaderLanguageChange = async (language) => {
    if (language !== getLanguage()) {
      await this.handleSelectLanguage(language);
      await this.props.fetchCountries();

      const translatedCountry = this.props.countries
        .toJS()
        .find((item) => item.country_code === this.state.selectedCountry.value);

      this.setState({ selectedCountry: { label: translatedCountry.name, value: translatedCountry.country_code } });
    }
  };

  getSelectedLanguage = () => this.props.languages?.toJS().find((lang) => lang.value === getLanguage().toLowerCase());

  handleSelectCountry = (country) => this.setState({ selectedCountry: country });

  componentDidCatch(error, errorInfo) {
    // Navigate via the injected navigate function
    this.navigate({ pathname: baseErrorPath, state: { error, errorInfo } });
  }

  render() {
    return (
      <SharedLayout
        header={
          <Header
            countries={this.props.countries}
            selectedCountry={this.state.selectedCountry}
            handleSelectCountry={this.handleSelectCountry}
            languages={this.props.languages}
            selectedLanguage={this.getSelectedLanguage()}
            handleSelectLanguage={this.handleHeaderLanguageChange}
            location={this.props.location}
            countriesLoaded={this.props.countriesLoaded}
            languagesLoaded={this.props.languagesLoaded}
          />
        }
        footer={<Footer />}
      >
        <Routes>
          <Route path={signInPath} element={<SignIn />} />
          <Route path={signOutPath} element={<SignOut />} />
          <Route path={contactUsPath} element={<ContactUs />} />
          <Route path={contactUsSuccessPath} element={<ContactUsSuccess />} />
          <Route path={restorePasswordPath} element={<RestorePassword />} />
          <Route path={recoverPasswordPath} element={<RecoverPassword />} />
          <Route path={restorePasswordSuccessPath} element={<RestorePasswordSuccess />} />
          <Route path={registrationPath} element={<Registration handleSelectCountry={this.handleSelectCountry} />} />
          <Route path={registrationSuccessPath} element={<RegistrationSuccess />} />
          <Route path={installationPath} element={<Installation />} />
          <Route path={helpPath} element={<Help />} />
          <Route
            path={termsConditionsPath}
            element={<TermsAndConditions country={this.state.selectedCountry.value} />}
          />
          <Route path={dataPrivacyPath} element={<DataPrivacy country={this.state.selectedCountry.value} />} />
          <Route path={imprintPath} element={<Imprint />} />
          <Route path={wechatCallbackPath} element={<WeChatCallback />} />
          <Route path={wechatInvitePath} element={<WechatInvite />} />
          <Route path={wechatAccountChoicePath} element={<WeChatAccountChoice />} />
          <Route path={wechatLoginLinkPath} element={<WeChatLoginLink />} />

          <Route path={baseErrorPath} element={<Error />} />

          <Route path={basePath} element={<Navigate to={signInPath} replace />} />
          <Route path="*" element={<Navigate to={notFoundPath} replace />} />
        </Routes>
      </SharedLayout>
    );
  }
}

Shared.propTypes = {
  countries: ImmutablePropTypes.list,
  fetchCountries: PropTypes.func.isRequired,
  countriesLoaded: PropTypes.bool,
  countriesLoading: PropTypes.bool,
  languages: ImmutablePropTypes.list,
  fetchLanguages: PropTypes.func.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  languagesLoaded: PropTypes.bool,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
};

Shared.defaultProps = {
  countries: null,
  countriesLoaded: false,
  countriesLoading: false,
  languages: null,
  languagesLoaded: false,
};

// Wrapper to inject navigate and location from hooks
function SharedWithRouter(props) {
  const navigate = useNavigate();
  const location = { pathname: window.location.pathname };

  // Create a ref to pass navigate to the class component
  const sharedRef = React.useRef(null);
  React.useEffect(() => {
    if (sharedRef.current) {
      sharedRef.current.navigate = navigate;
    }
  }, [navigate]);

  return <Shared ref={sharedRef} navigate={navigate} location={location} {...props} />;
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SharedWithRouter));
